import type { ComponentType, FC } from 'react';
import React from 'react';
import type { TFunction } from 'react-i18next';
import { useInitEssentials } from '../hooks/essentials';
import type { ExternalComponentProps } from '../exported-components/types';
import type Experiments from '@wix/wix-experiments';

export interface Essentials {
  t: TFunction;
  languageCode: string;
  experiments?: Experiments;
}

export const EssentialsContext = React.createContext<Essentials | undefined>(
  undefined,
);

interface EssentialsContextProps {
  essentials: Essentials;
  children: React.ReactNode;
}

export const EssentialsContextProvider: FC<EssentialsContextProps> = ({
  essentials,
  children,
}) => (
  <EssentialsContext.Provider value={essentials}>
    {children}
  </EssentialsContext.Provider>
);

export function withEssentialsContext<Props extends ExternalComponentProps>(
  Component: ComponentType<Props>,
) {
  return (props: Props) => {
    const { essentials, ready } = useInitEssentials({
      languageCode: props.languageCode ?? 'en',
      experiments: props?.experiments,
    });
    if (!ready) {
      return null;
    }

    return (
      <EssentialsContextProvider essentials={essentials}>
        <Component {...props} />
      </EssentialsContextProvider>
    );
  };
}

export const useEssentials = () => {
  const context = React.useContext(EssentialsContext);
  if (context === undefined) {
    throw new Error(
      'useEssentials must be used within a EssentialsContextProvider',
    );
  }
  return context;
};
